<template>
  <!-- Section header (full, with h1) -->
  <div v-if="!hideHeader" class="flex items-center gap-2 mb-3">
    <h1>{{ t('relatedTask') }}</h1>
    <button
      v-if="!localTask && !isCreating"
      class="px-3 rounded-lg bg-blue-200 hover:bg-blue-300 active:bg-blue-400 text-xl"
      type="button"
      @click="isCreating = true"
    >+</button>
  </div>

  <!-- Add button only (when header is owned by parent) -->
  <div v-else-if="!localTask && !isCreating" class="mb-3">
    <button
      class="px-3 rounded-lg bg-blue-200 hover:bg-blue-300 active:bg-blue-400 text-xl"
      type="button"
      @click="isCreating = true"
    >+</button>
  </div>

  <!-- Content: shown when a task exists OR when creating -->
  <div v-if="localTask || isCreating" class="border border-gray-200 rounded-lg p-4 bg-white">

    <!-- Top bar: meta + action buttons (mirrors TaskDialog header) -->
    <div class="flex items-center justify-between w-full mb-4 pr-1">
      <div class="flex items-center gap-2 flex-wrap">
        <template v-if="taskMeta.externalID">
          <span class="text-sm text-gray-400">#{{ taskMeta.externalID }}</span>
          <span class="text-sm text-gray-400">{{ t('createdBy') }}</span>
          <span class="text-sm font-semibold text-gray-700">{{ taskMeta.createdByName }}</span>
        </template>
      </div>
      <div class="flex items-center gap-2">
        <!-- Edit (view mode, existing task) -->
        <button
          v-if="localTask && !isEditing"
          @click="isEditing = true"
          type="button"
          class="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
          :title="t('edit')"
        >
          <Icon icon="mdi:pencil-outline" class="w-5 h-5" />
        </button>
        <!-- Save (edit / create mode — hidden in create when parent handles save) -->
        <button
          v-if="isEditing || (isCreating && !hideCreateSave)"
          @click="saveTask"
          :disabled="saving"
          type="button"
          class="flex items-center gap-2 bg-green-100 hover:bg-green-200 text-green-800 font-medium px-4 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
        >
          <Icon icon="mdi:content-save-outline" class="w-4 h-4" />
          {{ t('saveChanges') }}
        </button>
        <!-- Cancel (create mode only, no task yet) -->
        <button
          v-if="isCreating && !localTask"
          @click="cancelCreate"
          type="button"
          class="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        >
          <Icon icon="mdi:close" class="w-5 h-5" />
        </button>
        <!-- Delete (view mode, existing task) -->
        <button
          v-if="localTask && !isEditing"
          @click="deleteTask"
          type="button"
          class="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          :title="t('delete')"
        >
          <Icon icon="mdi:trash-can-outline" class="w-5 h-5" />
        </button>
      </div>
    </div>

    <!-- ── VIEW MODE ──────────────────────────────────────────── -->
    <template v-if="localTask && !isEditing">
      <!-- Name + status dropdown -->
      <div class="mb-5 flex items-start justify-between gap-4">
        <div>
          <p class="text-xs text-gray-400 mb-1">{{ t('taskName') }}</p>
          <p class="text-base font-bold text-gray-900">{{ form.name || '—' }}</p>
        </div>

        <el-dropdown trigger="click" @command="changeStatus" :disabled="changingStatus">
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-150 hover:brightness-95 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            :class="[
              STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.bg,
              STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.border
            ]"
            style="color: #4B5563;"
          >
            <Icon
              :icon="changingStatus ? 'mdi:loading' : STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.icon"
              class="w-4 h-4"
              :class="changingStatus ? 'animate-spin' : ''"
            />
            {{ t(statusLabelKeys[form.status] || 'toDo') }}
            <Icon icon="mdi:chevron-down" class="w-4 h-4 ms-auto" />
          </button>
          <template #dropdown>
            <el-dropdown-menu style="min-width: 160px;">
              <el-dropdown-item
                v-for="(cfg, key) in STATUS_CONFIG"
                :key="key"
                :command="key"
                :class="[cfg.baseBg, cfg.hoverBg, form.status === key ? 'font-semibold ' + cfg.bg : '']"
                style="color: #4B5563;"
              >
                <div class="flex items-center gap-2 w-full">
                  <Icon :icon="cfg.icon" class="w-4 h-4 flex-shrink-0" />
                  {{ t(statusLabelKeys[key] || 'toDo') }}
                  <Icon v-if="form.status === key" icon="mdi:check" class="w-3.5 h-3.5 ml-auto" />
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>

      <div class="mb-5">
        <p class="text-xs text-gray-400 mb-1">{{ t('description') }}</p>
        <p class="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{{ form.description || '—' }}</p>
      </div>

      <div class="mb-5">
        <p class="text-xs text-gray-400 mb-1">{{ t('dueDate') }}</p>
        <div class="flex items-center gap-1.5 text-sm text-gray-700">
          <Icon icon="mdi:calendar-outline" class="w-4 h-4 text-gray-400" />
          <span>{{ formatDateTime(form.dueDate as string | null) || '—' }}</span>
        </div>
      </div>

      <div class="mb-5">
        <p class="text-xs text-gray-400 mb-1">{{ t('assignTo') }}</p>
        <div class="flex items-center gap-2 mt-1">
          <div class="flex -space-x-1">
            <div
              v-for="assignee in assigneeUsers"
              :key="assignee.id"
              class="w-7 h-7 rounded-full border-2 border-white overflow-hidden bg-blue-100 flex items-center justify-center flex-shrink-0"
            >
              <img
                v-if="assignee.avatar"
                :src="storagePath + '/' + assignee.avatar"
                :alt="assignee.name"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-xs font-medium text-blue-600">
                {{ assignee.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
            </div>
          </div>
          <span class="text-sm font-bold text-gray-900">{{ assigneeNames || '—' }}</span>
        </div>
      </div>

      <!-- Comments -->
      <div class="border-t border-gray-200 pt-4 mt-2">
        <h4 class="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
          <Icon icon="mdi:comment-text-multiple-outline" class="w-4 h-4" />
          {{ t('comments') }}
        </h4>

        <div class="space-y-3 max-h-48 overflow-y-auto mb-3">
          <div v-for="comment in comments" :key="comment.id" class="flex gap-2 group">
            <div class="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
              <img
                v-if="comment.user?.avatar"
                :src="storagePath + '/' + comment.user.avatar"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-xs font-medium text-blue-600">
                {{ comment.user?.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-baseline gap-2">
                <span class="text-xs font-medium text-gray-800">{{ comment.user?.name || '' }}</span>
                <span class="text-xs text-gray-400">{{ formatCommentTime(comment.createdAt) }}</span>
              </div>
              <template v-if="editingCommentID === comment.id">
                <div class="flex gap-2 mt-1">
                  <el-input v-model="editingCommentText" size="small" class="flex-1" />
                  <el-button size="small" type="primary" @click="saveCommentEdit(comment.id)">{{ t('save') }}</el-button>
                  <el-button size="small" @click="editingCommentID = ''">{{ t('cancel') }}</el-button>
                </div>
              </template>
              <p v-else class="text-xs text-gray-600 mt-0.5 break-words">{{ comment.comment }}</p>
            </div>
            <div
              v-if="comment.user?.id === currentUserID"
              class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            >
              <button class="text-gray-400 hover:text-blue-500 p-0.5" type="button" @click="startEditComment(comment)">
                <Icon icon="mdi:pencil-outline" class="w-3.5 h-3.5" />
              </button>
              <button class="text-gray-400 hover:text-red-500 p-0.5" type="button" @click="deleteComment(comment.id)">
                <Icon icon="mdi:trash-can-outline" class="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div v-if="comments.length === 0" class="text-xs text-gray-400 text-center py-2">
            {{ t('noComments') }}
          </div>
        </div>

        <div class="flex gap-2">
          <el-input
            v-model="newComment"
            :placeholder="t('writeComment')"
            size="small"
            class="flex-1"
            @keyup.enter="submitComment"
          />
          <el-button size="small" type="primary" @click="submitComment" :disabled="!newComment.trim()">
            {{ t('send') }}
          </el-button>
        </div>
      </div>
    </template>

    <!-- ── EDIT / CREATE MODE ──────────────────────────────────── -->
    <el-form
      v-if="isEditing || isCreating"
      ref="formRef"
      :model="form"
      :rules="formRules"
      label-position="top"
      class="w-full [&_.el-form-item]:w-full [&_.el-date-editor]:w-full [&_.el-select]:w-full [&_.el-input]:w-full"
    >
      <el-form-item :label="t('taskName')" prop="name">
        <el-input v-model="form.name" :placeholder="t('taskName')" />
      </el-form-item>

      <el-form-item :label="t('description')">
        <el-input v-model="form.description" type="textarea" :rows="3" :placeholder="t('description')" />
      </el-form-item>

      <div class="flex items-end gap-3 mb-[18px] w-full">
        <el-form-item :label="t('dueDate')" class="flex-1 !mb-0 [&_.el-date-editor]:w-full">
          <el-date-picker
            v-model="form.dueDate"
            type="datetime"
            :placeholder="t('dueDate')"
            format="DD/MM/YYYY hh:mm A"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>

        <div class="flex-1 pb-0.5">
          <p class="text-xs text-gray-600 mb-2 font-medium">{{ t('status') }}</p>
          <el-dropdown trigger="click" @command="(key: string) => form.status = key" class="w-full">
            <button
              type="button"
              class="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-150 hover:brightness-95 active:scale-95 h-[32px]"
              :class="[
                STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.bg,
                STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.border
              ]"
              style="color: #4B5563;"
            >
              <Icon :icon="STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.icon" class="w-4 h-4" />
              {{ t(statusLabelKeys[form.status] || 'toDo') }}
              <Icon icon="mdi:chevron-down" class="w-4 h-4 ms-auto" />
            </button>
            <template #dropdown>
              <el-dropdown-menu style="min-width: 220px;">
                <el-dropdown-item
                  v-for="(cfg, key) in STATUS_CONFIG"
                  :key="key"
                  :command="key"
                  :class="[cfg.baseBg, cfg.hoverBg, form.status === key ? 'font-semibold ' + cfg.bg : '']"
                  style="color: #4B5563;"
                >
                  <div class="flex items-center gap-2 w-full">
                    <Icon :icon="cfg.icon" class="w-4 h-4 flex-shrink-0" />
                    {{ t(statusLabelKeys[key] || 'toDo') }}
                    <Icon v-if="form.status === key" icon="mdi:check" class="w-3.5 h-3.5 ml-auto" />
                  </div>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>

      <el-form-item :label="t('assignTo')" class="mb-2">
        <el-select v-model="form.assigneeIDs" multiple filterable :placeholder="t('assignTo')" class="w-full">
          <el-option
            v-for="user in users"
            :key="user.id"
            :label="user.name || user.username"
            :value="user.id"
          />
        </el-select>
      </el-form-item>
    </el-form>
  </div>

  <!-- Cashier PIN dialog -->
  <el-dialog v-model="pinDialogOpen" :title="t('enterPin')" width="300px" append-to-body>
    <el-input
      v-model="cashierPin"
      type="password"
      :placeholder="t('enterPin')"
      ref="pinInputRef"
      @keyup.enter="confirmPinAndComment"
    />
    <template #footer>
      <el-button @click="pinDialogOpen = false">{{ t('cancel') }}</el-button>
      <el-button type="primary" @click="confirmPinAndComment">{{ t('confirm') }}</el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, nextTick, PropType } from 'vue'
import { Icon } from '@iconify/vue'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import { useSettingsStore } from '@/stores/settingsStore'
import { ElMessage, ElMessageBox } from 'element-plus'
import Task, { TaskComment } from '@/models/hr/Task'
import User from '@/models/user/User'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

const STATUS_CONFIG = {
  todo:        { icon: 'mdi:format-list-bulleted',    bg: 'bg-gray-100',   baseBg: 'bg-gray-50',    border: 'border-gray-200',   hoverBg: 'hover:bg-gray-100'   },
  in_progress: { icon: 'mdi:circle-half-full',        bg: 'bg-yellow-100', baseBg: 'bg-yellow-50',  border: 'border-yellow-200', hoverBg: 'hover:bg-yellow-100' },
  done:        { icon: 'mdi:checkbox-marked-outline', bg: 'bg-green-100',  baseBg: 'bg-green-50',   border: 'border-green-200',  hoverBg: 'hover:bg-green-100'  },
  canceled:    { icon: 'mdi:close-circle-outline',    bg: 'bg-red-100',    baseBg: 'bg-red-50',     border: 'border-red-200',    hoverBg: 'hover:bg-red-100'    },
}

export default defineComponent({
  name: 'InlineTaskEditor',
  components: { Icon },
  props: {
    task:           { type: Object as PropType<Task | null>, default: null },
    users:          { type: Array as PropType<User[]>, default: () => [] },
    noteId:         { type: String, default: '' },
    hideHeader:     { type: Boolean, default: false },
    hideCreateSave: { type: Boolean, default: false },
  },
  emits: ['task-created', 'task-deleted', 'status-changed'],
  setup(props, { emit }) {
    const { t } = useI18n({
      messages: {
        en: {
          relatedTask: 'Related Task',
          taskName: 'Task Name', description: 'Description',
          dueDate: 'Date & Time', assignTo: 'Assign to',
          saveChanges: 'Save changes', edit: 'Edit', createdBy: 'created by',
          comments: 'Comments', noComments: 'No comments yet',
          writeComment: 'Write a comment...', send: 'Send',
          save: 'Save', cancel: 'Cancel', delete: 'Delete',
          taskNameRequired: 'Task name is required',
          toDo: 'To Do', inProgress: 'In Progress', done: 'Done', canceled: 'Canceled',
          status: 'Status', commentAdded: 'Comment added',
          enterPin: 'Enter cashier pin', confirm: 'Confirm', incorrectPin: 'Incorrect PIN',
        },
        ar: {
          relatedTask: 'المهمة المرتبطة',
          taskName: 'اسم المهمة', description: 'الوصف',
          dueDate: 'الموعد النهائي', assignTo: 'توكَل إلى',
          saveChanges: 'حفظ التغييرات', edit: 'تعديل', createdBy: 'أنشأها',
          comments: 'التعليقات', noComments: 'لا توجد تعليقات',
          writeComment: 'اكتب تعليقاً...', send: 'إرسال',
          save: 'حفظ', cancel: 'إلغاء', delete: 'حذف',
          taskNameRequired: 'اسم المهمة مطلوب',
          toDo: 'للتنفيذ', inProgress: 'قيد التنفيذ', done: 'منفَّذة', canceled: 'ملغيّة',
          status: 'الحالة', commentAdded: 'تم إضافة التعليق',
          enterPin: 'ادخل رمز الكاشير الخاص بك', confirm: 'تأكيد', incorrectPin: 'رمز الكاشير غير صحيح',
        }
      }
    })

    const http = useHTTP()
    const settingsStore = useSettingsStore()
    const storagePath = settingsStore.storagePath

    const currentUserID = computed<string>(() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}').id || '' } catch { return '' }
    })

    const hideCreateSave = computed(() => props.hideCreateSave)

    const localTask   = ref<Task | null>(props.task)
    const currentNoteID = ref(props.noteId)
    watch(() => props.noteId, val => { currentNoteID.value = val })
    const isCreating  = ref(false)
    const isEditing   = ref(false)
    const saving      = ref(false)
    const changingStatus = ref(false)
    const formRef     = ref()

    const taskMeta = ref({ externalID: '', createdByName: '' })
    const comments = ref<TaskComment[]>([])
    const newComment = ref('')
    const editingCommentID = ref('')
    const editingCommentText = ref('')
    const pinDialogOpen = ref(false)
    const cashierPin = ref('')
    const pinInputRef = ref()

    const statusLabelKeys: Record<string, string> = {
      todo: 'toDo', in_progress: 'inProgress', done: 'done', canceled: 'canceled'
    }

    const form = ref<{ name: string; description: string; dueDate: string | null; assigneeIDs: string[]; status: string }>({
      name: '', description: '', dueDate: null, assigneeIDs: [], status: 'todo'
    })

    const formRules = {
      name: [{ required: true, message: t('taskNameRequired'), trigger: 'blur' }]
    }

    const assigneeUsers = computed(() =>
      form.value.assigneeIDs.map(id => props.users.find(u => u.id === id)).filter(Boolean) as User[]
    )
    const assigneeNames = computed(() =>
      assigneeUsers.value.map(u => u.name || u.username).join(', ')
    )

    const formatDateTime = (date: string | null) => {
      if (!date) return ''
      return dayjs(date).format('DD/MM/YYYY hh:mm A')
    }

    const formatCommentTime = (date: string) => {
      if (!date) return ''
      return dayjs(date).fromNow()
    }

    const loadComments = async (id: string) => {
      try {
        const data = await http.get<TaskComment[]>(`/hr/tasks/comments?task_id=${id}`)
        if (data) comments.value = data
      } catch { /* ignore */ }
    }

    const loadFromTask = (task: Task) => {
      taskMeta.value = {
        externalID: (task as any).externalID || '',
        createdByName: task.createdByUser?.name || ''
      }
      let dueDate: string | null = null
      if (task.dueDate) {
        dueDate = dayjs(task.dueDate as unknown as string).format('YYYY-MM-DD HH:mm:ss')
      }
      form.value = {
        name: task.name || '',
        description: task.description || '',
        dueDate,
        assigneeIDs: task.assignees?.map(a => a.id || '') || [],
        status: task.status || 'todo'
      }
      if (task.id) loadComments(task.id)
    }

    // Initialize from prop
    watch(() => props.task, (task) => {
      localTask.value = task
      if (task) loadFromTask(task)
      else {
        form.value = { name: '', description: '', dueDate: null, assigneeIDs: [], status: 'todo' }
        taskMeta.value = { externalID: '', createdByName: '' }
        comments.value = []
      }
    }, { immediate: true })

    const cancelCreate = () => {
      isCreating.value = false
      form.value = { name: '', description: '', dueDate: null, assigneeIDs: [], status: 'todo' }
      formRef.value?.resetFields()
    }

    const saveTask = async () => {
      if (formRef.value) {
        try { await formRef.value.validate() } catch { return }
      }
      saving.value = true
      try {
        const payload: any = { ...form.value }
        if (payload.dueDate) {
          payload.dueDate = new Date(payload.dueDate).toISOString()
        }

        if (localTask.value?.id) {
          // Update existing task
          const updated = await http.put<Task>(`/hr/tasks/${localTask.value.id}`, { ...payload, id: localTask.value.id })
          if (updated) {
            localTask.value = updated
            loadFromTask(updated)
          }
          isEditing.value = false
        } else {
          // Create new task
          if (currentNoteID.value) payload.noteID = currentNoteID.value
          const created = await http.post<Task>('/hr/tasks', payload)
          if (created) {
            localTask.value = created
            loadFromTask(created)
            isCreating.value = false
            emit('task-created', created)
          }
        }
      } catch (e: any) {
        ElMessage.error(e?.body || e?.title || 'Error saving task')
      } finally {
        saving.value = false
      }
    }

    const deleteTask = async () => {
      if (!localTask.value) return
      try {
        await ElMessageBox.confirm('Delete this task?', 'Confirm', { type: 'warning' })
        await http.delete('/hr/tasks', { data: [{ id: localTask.value.id }] })
        localTask.value = null
        form.value = { name: '', description: '', dueDate: null, assigneeIDs: [], status: 'todo' }
        taskMeta.value = { externalID: '', createdByName: '' }
        comments.value = []
        emit('task-deleted')
      } catch { /* cancelled */ }
    }

    const changeStatus = async (newStatus: string) => {
      if (!localTask.value?.id || newStatus === form.value.status) return
      changingStatus.value = true
      try {
        await http.put(`/hr/tasks/${localTask.value.id}/status`, { status: newStatus })
        form.value.status = newStatus
        if (localTask.value) localTask.value.status = newStatus as any
        emit('status-changed', { taskId: localTask.value.id, newStatus })
      } catch (e) {
        console.error(e)
      } finally {
        changingStatus.value = false
      }
    }

    const submitComment = () => {
      if (!localTask.value?.id || !newComment.value.trim()) return
      const requirePin = settingsStore.getValue('is_cashier_pin_required') === 'true'
      if (requirePin) {
        cashierPin.value = ''
        pinDialogOpen.value = true
      } else {
        doAddComment('')
      }
    }

    const doAddComment = async (pin: string): Promise<boolean> => {
      if (!localTask.value?.id || !newComment.value.trim()) return false
      try {
        const headers: Record<string, string> = {}
        if (pin) headers.pin = pin
        const comment = await http.post<TaskComment>(
          `/hr/tasks/comments?task_id=${localTask.value.id}`,
          { comment: newComment.value },
          { headers }
        )
        if (comment) {
          comments.value.push(comment)
          newComment.value = ''
          ElMessage.success(t('commentAdded'))
          return true
        }
      } catch (e: any) {
        ElMessage.error(e?.body || e?.title || t('incorrectPin'))
      }
      return false
    }

    const confirmPinAndComment = async () => {
      const ok = await doAddComment(cashierPin.value)
      if (ok) pinDialogOpen.value = false
    }

    watch(pinDialogOpen, open => {
      if (open) nextTick(() => setTimeout(() => pinInputRef.value?.focus(), 50))
    })

    const startEditComment = (comment: TaskComment) => {
      editingCommentID.value = comment.id || ''
      editingCommentText.value = comment.comment || ''
    }

    const saveCommentEdit = async (id: string) => {
      try {
        const updated = await http.put<TaskComment>(`/hr/tasks/comments/${id}`, { comment: editingCommentText.value })
        if (updated) {
          const idx = comments.value.findIndex(c => c.id === id)
          if (idx !== -1) comments.value[idx] = updated
        }
        editingCommentID.value = ''
      } catch (e) { console.error(e) }
    }

    const deleteComment = async (id: string) => {
      try {
        await http.delete(`/hr/tasks/comments/${id}`)
        comments.value = comments.value.filter(c => c.id !== id)
      } catch (e) { console.error(e) }
    }

    return {
      t, localTask, isCreating, isEditing, saving, changingStatus, hideCreateSave,
      form, formRef, formRules, taskMeta, comments, newComment,
      editingCommentID, editingCommentText,
      pinDialogOpen, cashierPin, pinInputRef,
      storagePath, currentUserID, assigneeUsers, assigneeNames,
      STATUS_CONFIG, statusLabelKeys,
      formatDateTime, formatCommentTime,
      cancelCreate, saveTask, deleteTask, changeStatus,
      submitComment, confirmPinAndComment, startEditComment, saveCommentEdit, deleteComment,
    }
  }
})
</script>
